/**
 * Cálculo de diferencias entre stock teórico y cantidad contada, y resumen
 * agregado de una lista de inventario. Funciones puras, sin React.
 */
import { diferenciaDeLinea } from "./conteos";

/**
 * Calcula la diferencia entre el stock teórico y la cantidad contada de una
 * línea.
 *
 * Si la línea aún no se ha contado (cantidadContada es null/undefined),
 * `contado` es false y `diferencia` es null (NUNCA -stockTeorico: una línea
 * sin contar no es "falta todo el stock", es "todavía no se sabe").
 *
 * @param {number} stockTeorico - Stock que figuraba en el sistema al crear la lista
 * @param {number|null|undefined} cantidadContada - Cantidad física contada, o null/undefined si aún no se contó
 * @returns {{diferencia: number|null, cuadra: boolean, contado: boolean}} Diferencia (cantidadContada - stockTeorico), si cuadra exacto, y si la línea ya se contó
 */
export const calcularDiferencia = (stockTeorico, cantidadContada) => {
    const contado = cantidadContada !== null && cantidadContada !== undefined;

    if (!contado) {
        return { diferencia: null, cuadra: false, contado: false };
    }

    const diferencia = cantidadContada - stockTeorico;
    return { diferencia, cuadra: diferencia === 0, contado: true };
};

/**
 * Calcula el resumen agregado de una lista de inventario: cuántas líneas
 * tiene, cuántas ya se contaron, cuántas quedan pendientes y cuántas tienen
 * descuadre. Usa diferenciaDeLinea (conteos por nave) para no duplicar la
 * regla de qué cuenta como "contado".
 *
 * @param {Object} lista - Lista de inventario
 * @param {Array<{stockTeorico: number, cantidadContada: number|null}>} lista.lineas - Líneas de la lista
 * @returns {{totalLineas: number, contadas: number, pendientes: number, conDiferencia: number}} Resumen agregado
 */
export const resumenInventario = (lista) => {
    const lineas = lista?.lineas || [];

    const resultados = lineas.map(diferenciaDeLinea);

    const totalLineas = lineas.length;
    const contadas = resultados.filter((resultado) => resultado.contado).length;
    const conDiferencia = resultados.filter((resultado) => resultado.contado && !resultado.cuadra).length;

    return {
        totalLineas,
        contadas,
        pendientes: totalLineas - contadas,
        conDiferencia,
    };
};

/**
 * Mapea las líneas de un inventario a un array listo para pintar en modo
 * resultado (DetalleInventario, TASK-016), con la diferencia ya calculada
 * por línea. Usa diferenciaDeLinea (conteos por nave), la misma función que
 * resumenInventario.
 *
 * @param {Array<{stockTeorico: number, cantidadContada: number|null}>} lineas - Líneas del inventario
 * @returns {Array} Líneas originales con {diferencia, cuadra, contado} añadidos
 */
export const mapearLineasConDiferencia = (lineas = []) =>
    lineas.map((linea) => ({
        ...linea,
        ...diferenciaDeLinea(linea),
    }));

/**
 * Texto del resultado de un inventario a partir de resumenInventario. Lo que
 * cuadra se cuenta sobre lo contado, no sobre el total: con nada contado, decir
 * "0 de 1 cuadran" se leería como un descuadre.
 *
 * @param {{totalLineas: number, contadas: number, conDiferencia: number}} resumen - Salida de resumenInventario
 * @returns {string} Ej.: "2 de 3 contados · 1 cuadra · 1 con descuadre"
 */
export const describirResultado = ({ totalLineas, contadas, conDiferencia }) => {
    if (totalLineas === 0) return "Sin artículos";
    const progreso = `${contadas} de ${totalLineas} contados`;
    if (contadas === 0) return progreso;
    const cuadran = contadas - conDiferencia;
    return `${progreso} · ${cuadran} ${cuadran === 1 ? "cuadra" : "cuadran"} · ${conDiferencia} con descuadre`;
};
